/**
 * SRE Layout Preview
 * ==================
 * Component for selecting and previewing slab layout patterns.
 * 
 * NO AI - purely geometric pattern visualization.
 */

import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pattern visual representations (SVG paths)
const PatternIcon = ({ pattern, className }) => {
  const iconMap = {
    single: (
      <rect x="2" y="2" width="28" height="20" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
    ),
    bookmatch_h: (
      <>
        <rect x="2" y="2" width="13" height="20" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="17" y="2" width="13" height="20" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M8 5L5 11L8 17M24 5L27 11L24 17" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </>
    ),
    bookmatch_v: (
      <>
        <rect x="2" y="2" width="28" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="2" y="13" width="28" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M8 5H24M8 19H24" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </>
    ),
    bookmatch_quad: (
      <>
        <rect x="2" y="2" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="17" y="2" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="2" y="13" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="17" y="13" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="2" fill="none" />
      </>
    ),
    grid: (
      <>
        <rect x="2" y="2" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="12" y="2" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="22" y="2" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="2" y="13" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="12" y="13" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="22" y="13" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </>
    ),
    brick: (
      <>
        <rect x="2" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="14" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="26" y="2" width="4" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="2" y="13" width="5" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="9" y="13" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <rect x="21" y="13" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </>
    ),
  };
  
  return (
    <svg viewBox="0 0 32 24" className={className} fill="none">
      {iconMap[pattern] || iconMap.single}
    </svg>
  );
};

const LayoutSelector = ({
  selectedPattern = 'single',
  onPatternChange,
  surfaceWidthCm = 300,
  surfaceHeightCm = 200,
  slabWidthCm = 320,
  slabHeightCm = 180,
  groutWidthCm = 0,
  onDimensionsChange,
  showMetrics = true,
}) => {
  const [patterns, setPatterns] = useState([]);
  const [layoutPreview, setLayoutPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch available patterns
  useEffect(() => {
    const fetchPatterns = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/sre/patterns`);
        if (!response.ok) throw new Error('Failed to fetch patterns');
        const data = await response.json();
        setPatterns(data.patterns || []);
      } catch (err) {
        console.error('Error fetching patterns:', err);
        // Fallback patterns
        setPatterns([
          { id: 'single', name: 'Single Slab', description: 'Single slab scaled to fit' },
          { id: 'bookmatch_h', name: 'Bookmatch H', description: 'Horizontal mirror pattern' },
          { id: 'bookmatch_v', name: 'Bookmatch V', description: 'Vertical mirror pattern' },
          { id: 'bookmatch_quad', name: 'Bookmatch Quad', description: '4-way mirror' },
          { id: 'grid', name: 'Grid', description: 'Simple grid pattern' },
          { id: 'brick', name: 'Brick', description: 'Offset rows' },
        ]);
      }
    };
    fetchPatterns();
  }, []);
  
  // Fetch layout preview when params change
  useEffect(() => {
    const fetchPreview = async () => {
      if (!selectedPattern || surfaceWidthCm <= 0 || surfaceHeightCm <= 0) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/sre/preview-layout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            surface_width_cm: surfaceWidthCm,
            surface_height_cm: surfaceHeightCm,
            slab_width_cm: slabWidthCm,
            slab_height_cm: slabHeightCm,
            pattern: selectedPattern,
            grout_width_cm: groutWidthCm,
          }),
        });
        
        if (!response.ok) throw new Error('Failed to fetch layout preview');
        const data = await response.json();
        setLayoutPreview(data);
      } catch (err) {
        console.error('Error fetching layout preview:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchPreview, 300);
    return () => clearTimeout(debounce);
  }, [selectedPattern, surfaceWidthCm, surfaceHeightCm, slabWidthCm, slabHeightCm, groutWidthCm]);
  
  return (
    <div className="space-y-6" data-testid="sre-layout-selector">
      {/* Pattern Selection */}
      <div>
        <h3 className="text-xs font-medium text-[#A0A0A0] uppercase tracking-wide mb-3">Layout Pattern</h3>
        <div className="grid grid-cols-3 gap-2">
          {patterns.map((pattern) => (
            <button
              key={pattern.id}
              onClick={() => onPatternChange?.(pattern.id)}
              className={`
                p-3 rounded border transition-all
                ${selectedPattern === pattern.id
                  ? 'border-[#D6D6D6] bg-[#2A2A2A] text-white'
                  : 'border-[#2A2A2A] bg-[#1A1A1A] text-[#A0A0A0] hover:border-[#3A3A3A]'
                }
              `}
              data-testid={`sre-pattern-${pattern.id}`}
            >
              <PatternIcon
                pattern={pattern.id}
                className="w-full h-6 mb-2"
              />
              <div className="text-[10px] font-medium truncate">{pattern.name}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Dimension Inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] text-[#666666] uppercase tracking-wide mb-1.5">Surface Width (cm)</label>
          <input
            type="number"
            value={surfaceWidthCm}
            onChange={(e) => onDimensionsChange?.({ surfaceWidthCm: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-sm focus:outline-none focus:border-[#3A3A3A]"
            min="1"
            step="1"
            data-testid="sre-surface-width-input"
          />
        </div>
        <div>
          <label className="block text-[10px] text-[#666666] uppercase tracking-wide mb-1.5">Surface Height (cm)</label>
          <input
            type="number"
            value={surfaceHeightCm}
            onChange={(e) => onDimensionsChange?.({ surfaceHeightCm: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-sm focus:outline-none focus:border-[#3A3A3A]"
            min="1"
            step="1"
            data-testid="sre-surface-height-input"
          />
        </div>
        <div>
          <label className="block text-[10px] text-[#666666] uppercase tracking-wide mb-1.5">Slab Width (cm)</label>
          <input
            type="number"
            value={slabWidthCm}
            onChange={(e) => onDimensionsChange?.({ slabWidthCm: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-sm focus:outline-none focus:border-[#3A3A3A]"
            min="1"
            step="1"
            data-testid="sre-slab-width-input"
          />
        </div>
        <div>
          <label className="block text-[10px] text-[#666666] uppercase tracking-wide mb-1.5">Slab Height (cm)</label>
          <input
            type="number"
            value={slabHeightCm}
            onChange={(e) => onDimensionsChange?.({ slabHeightCm: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-sm focus:outline-none focus:border-[#3A3A3A]"
            min="1"
            step="1"
            data-testid="sre-slab-height-input"
          />
        </div>
      </div>
      
      {/* Grout Width */}
      <div>
        <label className="block text-[10px] text-[#666666] uppercase tracking-wide mb-1.5">Grout Width (cm)</label>
        <input
          type="number"
          value={groutWidthCm}
          onChange={(e) => onDimensionsChange?.({ groutWidthCm: parseFloat(e.target.value) || 0 })}
          className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-sm focus:outline-none focus:border-[#3A3A3A]"
          min="0"
          max="5"
          step="0.1"
          data-testid="sre-grout-width-input"
        />
      </div>
      
      {/* Metrics Display */}
      {showMetrics && layoutPreview && (
        <div className="p-4 bg-[#1A1A1A] rounded border border-[#2A2A2A]" data-testid="sre-layout-metrics">
          <h4 className="text-xs font-medium text-[#A0A0A0] uppercase tracking-wide mb-3">Layout Metrics</h4>
          {loading ? (
            <div className="text-[#666666] text-sm">Calculating...</div>
          ) : error ? (
            <div className="text-[#EF4444] text-sm">{error}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-[#666666]">Tiles:</span>
                <span className="ml-2 text-white font-medium">{layoutPreview.metrics?.tiles_count || 0}</span>
              </div>
              <div>
                <span className="text-[#666666]">Slabs:</span>
                <span className="ml-2 text-white font-medium">{layoutPreview.metrics?.slabs_needed || 0}</span>
              </div>
              <div>
                <span className="text-[#666666]">Area:</span>
                <span className="ml-2 text-white font-medium">{layoutPreview.metrics?.surface_area_m2?.toFixed(2) || 0} m²</span>
              </div>
              <div>
                <span className="text-[#666666]">Waste:</span>
                <span className="ml-2 text-[#FBBF24] font-medium">{layoutPreview.metrics?.waste_percent?.toFixed(1) || 0}%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LayoutSelector;
